// Keep this contract aligned with DocumentAnswerContent and the shared JSON corpus.
const blocks = new Set(['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'blockquote', 'pre', 'tr', 'td', 'ul', 'ol', 'section', 'article', 'header', 'footer', 'nav', 'aside']);

export function documentAnswerText(html) {
  const root = new DOMParser().parseFromString(String(html ?? ''), 'text/html').body;
  const walk = (node) => {
    if (node.nodeType === 3) return node.nodeValue;
    if (['SCRIPT', 'STYLE'].includes(node.nodeName)) return '';
    if (node.nodeName === 'BR') return ' ';
    return Array.from(node.childNodes).map(walk).join('') + (blocks.has(node.nodeName.toLowerCase()) ? ' ' : '');
  };
  const text = walk(root).replace(/[\u200b\ufeff]/g, '').replace(/\s+/g, ' ').trim();
  return /^[\s\u200c\u200d]*$/.test(text) ? '' : text;
}

function selectedOptions(answer) {
  return (answer.selectedOptions || []).filter(Boolean)
    .map((option) => `${option.serialNumber ?? ''}:${documentAnswerText(option.option)}`)
    .filter((option) => option !== ':').sort();
}

function attachment(url) {
  const value = String(url ?? '');
  const split = value.indexOf('?');
  if (split < 0) return value;
  const query = value.slice(split + 1).split('&')
    .filter((part) => !/^(x-goog-[^=]+|x-amz-[^=]+|GoogleAccessId|Signature|Expires)=/i.test(part)).sort().join('&');
  return value.slice(0, split) + (query ? `?${query}` : '');
}

export function canonicalDocumentAnswers(answers) {
  return (answers || []).filter((row) => row?.answer).map(({ serialNumber, answer }) => ({
    serial: String(serialNumber ?? ''), text: documentAnswerText(answer.answer), options: selectedOptions(answer),
    attachments: (answer.fileUrls || []).map(attachment), attachmentGeneration: answer.attachmentGeneration ?? 0,
    pages: answer.filePageIndexes || [],
  })).sort((a, b) => a.serial < b.serial ? -1 : a.serial > b.serial ? 1 : 0);
}

export function hasMeaningfulDocumentAnswers(answers) {
  return canonicalDocumentAnswers(answers).some((answer) => answer.text || answer.options.length);
}

// null means history has not been loaded; [] is a valid committed empty answer set.
export function hasDocumentRevision(answers, committedAnswers) {
  if (!Array.isArray(committedAnswers)) return null;
  return JSON.stringify(canonicalDocumentAnswers(answers)) !== JSON.stringify(canonicalDocumentAnswers(committedAnswers));
}

export function documentReviewIntent(attempt, submission, settings) {
  if (!attempt || submission?.type !== 'DOCUMENT' || !submission.editToken) return {};
  const canonicalSettings = { ...settings, successCriteriaIds: [...new Set(settings?.successCriteriaIds || [])].filter(Boolean).sort() };
  const signature = JSON.stringify([submission.id, submission.editToken, canonicalDocumentAnswers(submission.answers), canonicalSettings]);
  if (attempt.current?.signature === signature) return attempt.current.intent;
  const intent = { intent: ['REVIEWED', 'RESUBMISSION_REQUESTED'].includes(submission.status) ? 'REVISION' : 'INITIAL',
    expectedCurrentVersion: submission.currentVersion ?? 0, editToken: submission.editToken,
    operationId: globalThis.crypto?.randomUUID?.() || `review-${Date.now()}-${Math.random().toString(36).slice(2)}` };
  attempt.current = { signature, intent };
  return intent;
}
